import { faCircleXmark, faEdit, faEllipsisVertical, faPlus, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DCMDropdown from 'components/DropDown/DropDown.index';
import ClientModal from 'components/Modals/Client.modal';
import PromptModal from 'components/Modals/Prompt.modal';
import { useEffect, useState } from 'react';
import { Badge, Card, Container, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { AdminService } from 'Services/adminService';
import { removeEmptyValues } from 'helpers/request.helper';

export default function Clients() {
    const [clients, setClients] = useState<any[]>([]);
    const [showCreateModal, setShowCreateModal] = useState(false);

    async function getClients() {
        try {
            const res = await AdminService.getAllClients();
            if (res.status === 200) {
                setClients(res?.data || []);
            }
        } catch (err: any) {
            toast.error(err?.response?.data?.message || "Failed to fetch clients");
        }
    }

    async function createClient(data: any) {
        const payload = removeEmptyValues(data);
        try {
            const res = await AdminService.createClient(payload);
            if (res.status === 200) {
                toast.success("Client created successfully");
                setShowCreateModal(false);
                getClients();
            }
        } catch (err: any) {
            toast.error(err?.response?.data?.message || "Failed to create client");
        }
    }

    useEffect(() => {
        getClients();
    }, []);

    return (
        <Container fluid className="mt-3">
            <Card>
                <Card.Header className="d-flex justify-content-between align-items-center bg-white">
                    <h5 className="mb-0">Clients</h5>
                    <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => setShowCreateModal(true)}
                    >
                        <FontAwesomeIcon icon={faPlus} size="sm" className="me-2" />
                        Create Client
                    </button>
                </Card.Header>
                <Card.Body className="p-0">
                    <ClientTable tableData={clients} reload={getClients} />
                </Card.Body>
            </Card>

            <ClientModal
                show={showCreateModal}
                onHide={() => setShowCreateModal(false)}
                onSubmit={createClient}
                mode="create"
            />
        </Container>
    );
}

function ClientTable({ tableData, reload }: { tableData: any[]; reload: () => void }) {
    const [editClientId, setEditClientId] = useState("");
    const [editData, setEditData] = useState<any>(null);
    const [newPassword, setNewPassword] = useState("");

    async function handleToggleBlock(clientId: string) {
        try {
            const res = await AdminService.toggleBlockClient(clientId);
            if (res.status === 200) {
                toast.success("Client status updated");
                reload();
            }
        } catch (err: any) {
            toast.error(err?.response?.data?.message || "Failed to update client status");
        }
    }

    async function resetPassword(clientId: string) {
        try {
            const res = await AdminService.resetClientPassword(clientId);
            if (res.status === 200) {
                setNewPassword(res.data?.password);
            }
        } catch (err: any) {
            toast.error(err?.response?.data?.message || "Failed to reset password");
        }
    }

    async function handleUpdate(data_: any) {
        if (!data_?.name || !data_?.email) {
            toast.error("Name and email are required");
            return;
        }
        const payload = removeEmptyValues({
            name: data_?.name,
            email: data_?.email,
            mobile: data_?.mobile,
        });

        try {
            const res = await AdminService.updateClient(editClientId, payload);
            if (res.status === 200) {
                toast.success("Client updated successfully");
                setEditClientId("");
                reload();
            }
        } catch (err: any) {
            toast.error(err?.response?.data?.message || "Failed to update client");
        }
    }

    return (
        <>
            <Table hover responsive className="mb-0 align-middle">
                <thead className="table-light">
                    <tr>
                        <th className="fs-12">#</th>
                        <th className="fs-12">Name</th>
                        <th className="fs-12 text-center">Email</th>
                        <th className="fs-12 text-center">Phone</th>
                        <th className="fs-12 text-center">Status</th>
                        <th className="fs-12 text-center">Suspended</th>
                        <th className="fs-12"></th>
                    </tr>
                </thead>
                <tbody>
                    {tableData?.length > 0 ? (
                        tableData.map((client, index) => (
                            <tr key={client._id}>
                                <td className="fs-12">{index + 1}</td>
                                <td className="fs-12">{client?.name || 'N/A'}</td>
                                <td className="fs-12 text-center">{client?.email || 'N/A'}</td>
                                <td className="fs-12 text-center">{client?.mobile || 'N/A'}</td>
                                <td className="fs-12 text-center">
                                    <Badge bg={client?.status === 'ACTIVE' ? 'success' : 'secondary'}>
                                        {client?.status}
                                    </Badge>
                                </td>
                                <td className="fs-12 text-center">
                                    <Badge bg={client?.isSuspended ? 'danger' : 'success'}>
                                        {client?.isSuspended ? 'Yes' : 'No'}
                                    </Badge>
                                </td>
                                <td>
                                    <DCMDropdown
                                        menuItems={[
                                            {
                                                label: (
                                                    <div className="text-secondary">
                                                        <FontAwesomeIcon icon={faCircleXmark} className="me-2" />
                                                        {client?.isSuspended ? 'Unsuspend' : 'Suspend'}
                                                    </div>
                                                ),
                                                onClick: () => handleToggleBlock(client?._id),
                                            },
                                            {
                                                label: (
                                                    <div className="text-secondary">
                                                        <FontAwesomeIcon icon={faRefresh} className="me-2" />
                                                        Reset Password
                                                    </div>
                                                ),
                                                onClick: () => resetPassword(client?._id),
                                            },
                                            {
                                                label: (
                                                    <div className="text-secondary">
                                                        <FontAwesomeIcon icon={faEdit} className="me-2" />
                                                        Edit
                                                    </div>
                                                ),
                                                onClick: () => {
                                                    setEditClientId(client?._id);
                                                    setEditData(client);
                                                },
                                            },
                                        ]}
                                        menuLabel={
                                            <FontAwesomeIcon icon={faEllipsisVertical} className="cursor-pointer" />
                                        }
                                        title={
                                            <div className="text-primary fw-bold">Actions</div>
                                        }
                                    />
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={7} className="text-center py-4 text-muted">
                                No clients found
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>

            <PromptModal
                show={newPassword !== ''}
                setShow={() => setNewPassword('')}
                body={
                    <div>
                        Password Changed to <b>{newPassword}</b>
                    </div>
                }
            />

            <ClientModal
                show={editClientId !== ''}
                onHide={() => setEditClientId('')}
                onSubmit={handleUpdate}
                mode="update"
                initialData={
                    editData
                        ? { name: editData?.name, email: editData?.email, mobile: editData?.mobile }
                        : {}
                }
            />
        </>
    );
}
